import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios.js';

// material-ui
import {
  Button,
  FormHelperText,
  Grid,Box,
  Link,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import LockResetOutlined from '@mui/icons-material/LockResetOutlined';

import AuthCard from './AuthCard';
import AuthFooter from 'components/cards/AuthFooter';

// assets
import AuthBackground from 'assets/images/auth/AuthBackground';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

import AnimateButton from 'components/@extended/AnimateButton';
import { completeLogin } from 'utils/completeLogin';

// assets
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

const RESEND_COOLDOWN_SECONDS = 60;

// ==============================|| RESET MPIN (forgot MPIN, via OTP) ||============================== //

const ResetMpin = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const MPINToken = location.state && location.state.MPINToken;
  const MaskedMobileNumber = location.state && location.state.MaskedMobileNumber;

  // 'request' -> waiting for the person to ask for a code
  // 'reset'   -> code sent, collecting OTP + new MPIN
  const [stage, setStage] = useState('request');
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [showMpin, setShowMpin] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Reached this page directly without coming from the MPIN screen - there's
  // no account to reset a code for, send them back to start over.
  if (!MPINToken) {
    return <Navigate to="/login" replace />;
  }

  if (redirect) {
    return <Navigate to="/dashboard" replace />;
  }

  const requestOtp = async () => {
    setRequesting(true);
    setRequestError('');
    try {
      const response = await axios.post('/request-mpin-reset-otp', { MPINToken });
      if (response.data.Status === 0) {
        setRequestError(response.data.Message);
      } else {
        setStage('reset');
        setCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      setRequestError(err.message);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <>
    <Box sx={{ minHeight: '100vh' }}>
            <AuthBackground />
            <Grid
              container
              direction="column"
              justifyContent="flex-end"
              sx={{
                minHeight: '100vh'
              }}
            >
              <Grid item xs={12}>
                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{ minHeight: { xs: 'calc(100vh - 134px)', md: 'calc(100vh - 112px)' } }}
                >
                  <Grid item>
                  <AuthCard>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LockResetOutlined color="primary" />
          <Typography variant="h5">Reset your MPIN</Typography>
        </Stack>
        <Typography color="text.secondary">
          {stage === 'request'
            ? MaskedMobileNumber
              ? `We'll send a one-time code to ${MaskedMobileNumber} to confirm it's you.`
              : "We'll send a one-time code to your registered mobile number to confirm it's you."
            : 'Enter the code we sent you, then choose a new 4-digit MPIN.'}
        </Typography>
      </Stack>

      {stage === 'request' ? (
        <Grid container spacing={3}>
          {requestError && (
            <Grid item xs={12}>
              <FormHelperText error>{requestError}</FormHelperText>
            </Grid>
          )}
          <Grid item xs={12}>
            <AnimateButton>
              <Button
                disableElevation
                disabled={requesting}
                fullWidth
                size="large"
                variant="contained"
                color="primary"
                onClick={requestOtp}
              >
                Send Code
              </Button>
            </AnimateButton>
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="center">
            <Link variant="h6" component={RouterLink} to="/login" color="text.primary">
              Back to login
            </Link>
          </Grid>
        </Grid>
      ) : (
        <Formik
          initialValues={{ OTP: '', NewMPIN: '', ConfirmMPIN: '', submit: null }}
          validationSchema={Yup.object().shape({
            OTP: Yup.string()
              .matches(/^\d{4,6}$/, 'Enter the code exactly as sent')
              .required('The code is required'),
            NewMPIN: Yup.string()
              .matches(/^\d{4}$/, 'MPIN must be exactly 4 digits')
              .required('Choose a new MPIN'),
            ConfirmMPIN: Yup.string()
              .oneOf([Yup.ref('NewMPIN')], 'MPINs do not match')
              .required('Confirm your new MPIN')
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              const response = await axios.post('/reset-mpin', {
                MPINToken,
                OTP: values.OTP,
                NewMPIN: values.NewMPIN
              });

              if (response.data.Status === 0) {
                setStatus({ success: false });
                setErrors({ submit: response.data.Message });
                setSubmitting(false);
              } else {
                setStatus({ success: true });
                completeLogin(response.data);
                setRedirect(true);
              }
            } catch (err) {
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setSubmitting(false);
            }
          }}
        >
          {({ errors, handleBlur, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="reset-mpin-otp">Verification Code</InputLabel>
                    <OutlinedInput
                      id="reset-mpin-otp"
                      name="OTP"
                      inputMode="numeric"
                      autoFocus
                      value={values.OTP}
                      onBlur={handleBlur}
                      onChange={(e) => setFieldValue('OTP', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter the code"
                      fullWidth
                      error={Boolean(touched.OTP && errors.OTP)}
                    />
                    {touched.OTP && errors.OTP && (
                      <FormHelperText error id="reset-mpin-otp-error">
                        {errors.OTP}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="reset-mpin-new">New MPIN</InputLabel>
                    <OutlinedInput
                      id="reset-mpin-new"
                      name="NewMPIN"
                      type={showMpin ? 'text' : 'password'}
                      inputMode="numeric"
                      value={values.NewMPIN}
                      onBlur={handleBlur}
                      onChange={(e) => setFieldValue('NewMPIN', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="4 digit PIN"
                      fullWidth
                      error={Boolean(touched.NewMPIN && errors.NewMPIN)}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showMpin ? 'Hide MPIN' : 'Show MPIN'}
                            onClick={() => setShowMpin((prev) => !prev)}
                            edge="end"
                          >
                            {showMpin ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {touched.NewMPIN && errors.NewMPIN && (
                      <FormHelperText error id="reset-mpin-new-error">
                        {errors.NewMPIN}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="reset-mpin-confirm">Confirm New MPIN</InputLabel>
                    <OutlinedInput
                      id="reset-mpin-confirm"
                      name="ConfirmMPIN"
                      type={showMpin ? 'text' : 'password'}
                      inputMode="numeric"
                      value={values.ConfirmMPIN}
                      onBlur={handleBlur}
                      onChange={(e) => setFieldValue('ConfirmMPIN', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="Re-enter PIN"
                      fullWidth
                      error={Boolean(touched.ConfirmMPIN && errors.ConfirmMPIN)}
                    />
                    {touched.ConfirmMPIN && errors.ConfirmMPIN && (
                      <FormHelperText error id="reset-mpin-confirm-error">
                        {errors.ConfirmMPIN}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                {errors.submit && (
                  <Grid item xs={12}>
                    <FormHelperText error>{errors.submit}</FormHelperText>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <AnimateButton>
                    <Button disableElevation disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained" color="primary">
                      Reset MPIN &amp; Continue
                    </Button>
                  </AnimateButton>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Link
                      variant="h6"
                      component="button"
                      type="button"
                      color={cooldown > 0 ? 'text.disabled' : 'text.primary'}
                      onClick={cooldown > 0 ? undefined : requestOtp}
                      sx={{ cursor: cooldown > 0 ? 'default' : 'pointer' }}
                    >
                      {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                    </Link>
                    <Link variant="h6" component={RouterLink} to="/login" color="text.primary">
                      Back to login
                    </Link>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>

      )}
        </AuthCard>
         </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ m: 3, mt: 1 }}>
                <AuthFooter />
              </Grid>
            </Grid>
          </Box>
    </>

  );
};

export default ResetMpin;
